import {
  defaultConfig,
  type AcousticConfig,
  type ReceiverState,
} from './config';
import { AcousticProtocol, type DecodedFrame } from './protocol';
import { Hamming } from './hamming';
import type { MainToWorklet, WorkletToMain } from './worklet-protocol';

type StateFn = (state: ReceiverState) => void;
type ProgressFn = (current: number, total: number) => void;
type FrameFn = (payload: string, crcOk: boolean, result: DecodedFrame) => void;
type MessageFn = (msg: string) => void;
type SymbolFn = (symbol: number, state: ReceiverState) => void;

const CALIBRATION_MS = 500;

export interface FilePlaybackOptions {
  audible: boolean;
}

export class AcousticReceiver {
  audioCtx: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private sourceNode: AudioNode | null = null;
  private bufferSource: AudioBufferSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private audible = false;

  state: ReceiverState = 'idle';
  config: AcousticConfig = { ...defaultConfig };

  private dataSymbols: number[] = [];
  private preambleCount = 0;
  private lastSymbol = -1;
  private syncBuffer: number[] = [];
  private noiseFloor = 0;
  private expectedDataSymbols = 0;
  private silenceCount = 0;
  private preambleErrors = 0;
  private decoding = false;
  private resetTimer: number | null = null;
  autoResetDelay = true;

  onStateChange: StateFn | null = null;
  onProgress: ProgressFn | null = null;
  onFrameComplete: FrameFn | null = null;
  onError: MessageFn | null = null;
  onDebug: MessageFn | null = null;
  onSymbol: SymbolFn | null = null;
  onPlaybackEnd: (() => void) | null = null;

  private setState(s: ReceiverState): void {
    this.state = s;
    this.onStateChange?.(s);
  }

  private scheduleResetToListening(delayMs: number): void {
    if (this.resetTimer !== null) {
      window.clearTimeout(this.resetTimer);
    }
    this.resetTimer = window.setTimeout(() => {
      this.resetTimer = null;
      this.resetToListening();
    }, delayMs);
  }

  private postToWorklet(msg: MainToWorklet): void {
    this.workletNode?.port.postMessage(msg);
  }

  async start(config?: Partial<AcousticConfig>): Promise<void> {
    if (this.state !== 'idle') return;
    Object.assign(this.config, config);

    if (!(await this.initAudioContextAndWorklet())) return;

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
    } catch (e) {
      this.onError?.('Microphone access denied: ' + (e as Error).message);
      this.cleanupAudio();
      return;
    }

    const ctx = this.audioCtx!;
    const micSource = ctx.createMediaStreamSource(this.mediaStream);
    this.sourceNode = micSource;

    this.attachAnalyserAndWorklet(this.sourceNode);
    this.resetReceptionState();
    this.kickoffWorklet();
  }

  /**
   * Decode an audio file (WAV or anything decodeAudioData accepts) through
   * the same Goertzel pipeline as the live mic. If options.audible is true,
   * the decoded buffer is also routed to the destination so the user hears
   * the FSK tones during playback.
   */
  async startFromFile(
    file: File,
    config?: Partial<AcousticConfig>,
    options: FilePlaybackOptions = { audible: false },
  ): Promise<void> {
    if (this.state !== 'idle') this.stop();
    Object.assign(this.config, config);

    if (!(await this.initAudioContextAndWorklet())) return;

    let audioBuffer: AudioBuffer;
    try {
      const arrayBuffer = await file.arrayBuffer();
      audioBuffer = await this.audioCtx!.decodeAudioData(arrayBuffer);
    } catch (e) {
      this.onError?.('Could not decode audio file: ' + (e as Error).message);
      this.cleanupAudio();
      return;
    }

    const ctx = this.audioCtx!;
    const bufferSource = ctx.createBufferSource();
    bufferSource.buffer = audioBuffer;
    this.bufferSource = bufferSource;
    this.sourceNode = bufferSource;

    this.attachAnalyserAndWorklet(bufferSource);

    this.audible = options.audible;
    if (this.audible) {
      bufferSource.connect(ctx.destination);
    }

    bufferSource.onended = () => {
      this.onPlaybackEnd?.();
      window.setTimeout(() => {
        if (this.bufferSource === bufferSource) {
          this.stop();
        }
      }, 400);
    };

    this.resetReceptionState();
    // For file import we skip calibration: there is no ambient noise to sample,
    // and any sampling window would catch the FSK signal itself, inflating the
    // noise floor and silencing detection.
    this.noiseFloor = this.config.minMagnitude;
    this.postToWorklet({
      type: 'init',
      sampleRate: this.config.sampleRate,
      freqs: this.config.freqs,
      baudRate: this.config.baudRate,
      magnitudeRatio: this.config.magnitudeRatio,
      minMagnitude: this.config.minMagnitude,
    });
    this.postToWorklet({ type: 'startListening' });
    this.setState('listening');
    bufferSource.start();
  }

  /**
   * Toggle whether the imported audio plays out the speakers during decode.
   * Safe to call mid-playback: connect/disconnect the AudioBufferSourceNode
   * to ctx.destination without disturbing the worklet decode path.
   */
  setAudible(audible: boolean): void {
    if (!this.audioCtx || !this.bufferSource) {
      this.audible = audible;
      return;
    }
    if (audible === this.audible) return;
    try {
      if (audible) {
        this.bufferSource.connect(this.audioCtx.destination);
      } else {
        this.bufferSource.disconnect(this.audioCtx.destination);
      }
      this.audible = audible;
    } catch {
      /* already in target state, just sync our flag */
      this.audible = audible;
    }
  }

  isAudible(): boolean {
    return this.audible;
  }

  private async initAudioContextAndWorklet(): Promise<boolean> {
    try {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      this.audioCtx = new Ctor();
      if (this.audioCtx.state === 'suspended') await this.audioCtx.resume();
      this.config.sampleRate = this.audioCtx.sampleRate;
    } catch (e) {
      this.onError?.('Could not start AudioContext: ' + (e as Error).message);
      return false;
    }

    try {
      await this.audioCtx.audioWorklet.addModule('/goertzel-worklet.js');
    } catch (e) {
      this.onError?.('Could not load AudioWorklet module: ' + (e as Error).message);
      this.cleanupAudio();
      return false;
    }
    return true;
  }

  private attachAnalyserAndWorklet(source: AudioNode): void {
    const ctx = this.audioCtx!;
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 2048;
    source.connect(this.analyser);

    this.workletNode = new AudioWorkletNode(ctx, 'goertzel-processor', {
      numberOfInputs: 1,
      numberOfOutputs: 0,
    });
    this.workletNode.port.onmessage = (ev: MessageEvent) =>
      this.handleWorkletMessage(ev.data as WorkletToMain);
    source.connect(this.workletNode);
  }

  private resetReceptionState(): void {
    this.dataSymbols = [];
    this.preambleCount = 0;
    this.lastSymbol = -1;
    this.syncBuffer = [];
    this.silenceCount = 0;
    this.preambleErrors = 0;
    this.expectedDataSymbols = 0;
    this.decoding = false;
  }

  private kickoffWorklet(): void {
    this.postToWorklet({
      type: 'init',
      sampleRate: this.config.sampleRate,
      freqs: this.config.freqs,
      baudRate: this.config.baudRate,
      magnitudeRatio: this.config.magnitudeRatio,
      minMagnitude: this.config.minMagnitude,
    });
    this.postToWorklet({ type: 'startCalibration', durationMs: CALIBRATION_MS });
    this.setState('calibrating');
  }

  stop(): void {
    if (this.resetTimer !== null) {
      window.clearTimeout(this.resetTimer);
      this.resetTimer = null;
    }
    if (this.bufferSource) {
      try { this.bufferSource.onended = null; } catch { /* noop */ }
      try { this.bufferSource.stop(); } catch { /* noop */ }
      try { this.bufferSource.disconnect(); } catch { /* noop */ }
      this.bufferSource = null;
    }
    this.audible = false;
    if (this.workletNode) {
      this.postToWorklet({ type: 'stop' });
      this.workletNode.port.onmessage = null;
      try { this.workletNode.disconnect(); } catch { /* noop */ }
      this.workletNode = null;
    }
    this.cleanupAudio();
    this.setState('idle');
  }

  private cleanupAudio(): void {
    if (this.sourceNode) {
      try { this.sourceNode.disconnect(); } catch { /* noop */ }
      this.sourceNode = null;
    }
    if (this.analyser) {
      try { this.analyser.disconnect(); } catch { /* noop */ }
      this.analyser = null;
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop());
      this.mediaStream = null;
    }
    if (this.audioCtx) {
      void this.audioCtx.close();
      this.audioCtx = null;
    }
  }

  private handleWorkletMessage(msg: WorkletToMain): void {
    switch (msg.type) {
      case 'ready':
        return;
      case 'calibrationDone':
        this.finishCalibration(msg.mags);
        return;
      case 'silence':
        this.handleSilence();
        return;
      case 'symbol':
        this.handleSymbol(msg.symbol, msg.mags, msg.maxMag);
        return;
    }
  }

  private finishCalibration(mags: number[]): void {
    if (mags.length > 0) {
      const avg = mags.reduce((a, b) => a + b, 0) / mags.length;
      const max = Math.max(...mags);
      this.noiseFloor = Math.max(avg * 3, max * 2, this.config.minMagnitude);
      this.onDebug?.(
        'Noise floor: ' +
          this.noiseFloor.toFixed(2) +
          ' (avg=' +
          avg.toFixed(2) +
          ', max=' +
          max.toFixed(2) +
          ')',
      );
    } else {
      this.noiseFloor = this.config.minMagnitude;
    }
    this.postToWorklet({ type: 'startListening' });
    this.setState('listening');
  }

  private handleSilence(): void {
    if (
      this.state === 'complete' ||
      this.state === 'error' ||
      this.state === 'calibrating'
    ) {
      return;
    }
    if (this.state === 'receiving') {
      this.silenceCount++;
      if (this.silenceCount > 5) {
        this.onDebug?.('Signal lost during receiving, aborting');
        this.onError?.('Signal lost');
        this.setState('error');
        if (this.autoResetDelay) {
          this.scheduleResetToListening(1000);
        }
      }
    }
  }

  private handleSymbol(symbol: number, mags: number[], maxMag: number): void {
    if (
      this.state === 'complete' ||
      this.state === 'error' ||
      this.state === 'calibrating' ||
      this.decoding
    ) {
      return;
    }
    this.silenceCount = 0;
    if (maxMag < this.noiseFloor && this.state === 'listening') return;

    if (this.onDebug && this.state !== 'listening') {
      const magStr = mags.map((m, i) => `F${i}=${m.toFixed(1)}`).join(' ');
      this.onDebug(`${magStr} sym=${symbol}`);
    }

    if (symbol === -1) return;
    this.onSymbol?.(symbol, this.state);

    switch (this.state) {
      case 'listening':
        this.handleListening(symbol);
        break;
      case 'preamble':
        this.handlePreamble(symbol);
        break;
      case 'sync':
        this.handleSync(symbol);
        break;
      case 'receiving':
        void this.handleReceiving(symbol);
        break;
    }
  }

  private handleListening(symbol: number): void {
    if (this.lastSymbol === -1) {
      this.lastSymbol = symbol;
      this.preambleCount = 1;
      this.preambleErrors = 0;
      return;
    }
    if (symbol !== this.lastSymbol) {
      this.preambleCount++;
      if (this.preambleCount >= this.config.preambleMinCycles) {
        this.setState('preamble');
        this.onDebug?.('Preamble detected, count: ' + this.preambleCount);
      }
    } else {
      this.preambleErrors++;
      if (this.preambleErrors > this.config.preambleMaxErrors) {
        this.preambleCount = 1;
        this.preambleErrors = 0;
      }
    }
    this.lastSymbol = symbol;
  }

  private handlePreamble(symbol: number): void {
    if (symbol !== this.lastSymbol) {
      this.preambleCount++;
      this.lastSymbol = symbol;
    } else {
      this.syncBuffer = [this.lastSymbol, symbol];
      this.lastSymbol = symbol;
      this.setState('sync');
      this.onDebug?.(
        'Preamble end, looking for sync. Count was: ' + this.preambleCount,
      );
    }
  }

  private handleSync(symbol: number): void {
    this.syncBuffer.push(symbol);
    const sw = this.config.syncWord;

    if (this.syncBuffer.length >= sw.length) {
      const tail = this.syncBuffer.slice(-sw.length);
      let match = true;
      for (let i = 0; i < sw.length; i++) {
        if (tail[i] !== sw[i]) {
          match = false;
          break;
        }
      }
      if (match) {
        this.dataSymbols = [];
        this.expectedDataSymbols = 0;
        this.setState('receiving');
        this.onDebug?.('Sync word found! Receiving data...');
        return;
      }
    }

    if (this.syncBuffer.length > 20) {
      this.onDebug?.('Sync timeout, resetting');
      this.resetToListening();
    }
  }

  private async handleReceiving(symbol: number): Promise<void> {
    this.dataSymbols.push(symbol);

    const HAMMING_LEN_SYMBOLS = 14;
    if (
      this.dataSymbols.length === HAMMING_LEN_SYMBOLS &&
      this.expectedDataSymbols === 0
    ) {
      const codedBits = AcousticProtocol.symbolsToBits(
        this.dataSymbols.slice(0, HAMMING_LEN_SYMBOLS),
        2,
      );
      const { data: lenDataBits } = Hamming.decode(codedBits);
      const lenBytes = AcousticProtocol.bitsToBytes(lenDataBits.slice(0, 16));
      const payloadLen = (lenBytes[0] << 8) | lenBytes[1];
      if (payloadLen > this.config.maxPayload || payloadLen === 0) {
        this.onDebug?.('Invalid length: ' + payloadLen);
        this.onError?.('Invalid payload length: ' + payloadLen);
        this.resetToListening();
        return;
      }
      const totalDataBits = 16 + 8 + payloadLen * 8 + 16;
      const totalCodedBits = Hamming.codedLength(totalDataBits);
      const dataSymbolCount = Math.ceil(totalCodedBits / 2);
      this.expectedDataSymbols = dataSymbolCount + this.config.endWord.length;
      this.onDebug?.(
        'Payload: ' +
          payloadLen +
          ' bytes, expecting ' +
          this.expectedDataSymbols +
          ' symbols',
      );
    }

    if (this.expectedDataSymbols > 0 && this.onProgress) {
      this.onProgress(this.dataSymbols.length, this.expectedDataSymbols);
    }

    if (
      this.expectedDataSymbols > 0 &&
      this.dataSymbols.length >= this.expectedDataSymbols
    ) {
      this.decoding = true;
      const dataSymCount =
        this.expectedDataSymbols - this.config.endWord.length;
      const codedBits = AcousticProtocol.symbolsToBits(
        this.dataSymbols.slice(0, dataSymCount),
        2,
      );
      let result: DecodedFrame;
      try {
        result = await AcousticProtocol.decodeFrame(codedBits);
      } catch (e) {
        this.onError?.('Decode error: ' + (e as Error).message);
        this.setState('error');
        if (this.autoResetDelay) {
          this.scheduleResetToListening(1000);
        }
        return;
      }
      this.onDebug?.(
        'Frame complete. CRC: ' +
          (result.crcOk ? 'OK' : 'FAIL') +
          (result.compressed ? ' | deflate' : ' | raw') +
          (result.corrected ? ' | ' + result.corrected + ' bits corrected' : ''),
      );
      this.onFrameComplete?.(result.payload, result.crcOk, result);
      if (!result.crcOk || result.error) {
        if (result.error && result.crcOk) this.onError?.(result.error);
        this.setState('error');
        if (this.autoResetDelay) {
          this.scheduleResetToListening(1000);
        }
      } else {
        this.setState('complete');
        if (this.autoResetDelay) {
          this.scheduleResetToListening(2000);
        }
      }
    }
  }

  private resetToListening(): void {
    if (!this.audioCtx || !this.workletNode) return;
    this.dataSymbols = [];
    this.syncBuffer = [];
    this.preambleCount = 0;
    this.lastSymbol = -1;
    this.expectedDataSymbols = 0;
    this.silenceCount = 0;
    this.preambleErrors = 0;
    this.decoding = false;
    this.setState('listening');
  }

  getAnalyser(): AnalyserNode | null {
    return this.analyser;
  }
}
